import { KeyInfo, TimingInfo } from "./MasterComposer";
import { PhraseManager } from "./PhraseManager";

/**
 * Manages musical key and harmony for the phrase-based music system.
 *
 * Provides root note, scale information, and simple I-V chord progressions
 * that follow the phrase structure. Creates harmonic movement that supports
 * funky music composition with stable grooves and exciting fills.
 *
 * Harmonic Logic:
 * - Groove portions: I chord (root note + major scale) for stable foundation
 * - Fill portions: V chord (fifth + mixolydian scale) for tension and excitement
 * - Creates natural groove-to-fill musical flow within each phrase
 * - V chord provides tension that resolves when next phrase begins
 */
export class KeyManager {
  /** Current root note as MIDI number (default: C4) */
  private rootNote: number = 60;

  /** Major scale intervals from root */
  private readonly majorScale = [0, 2, 4, 5, 7, 9, 11];

  /** Mixolydian scale intervals (for V chord) */
  private readonly mixolydianScale = [0, 2, 4, 5, 7, 9, 10];

  /**
   * Get key and harmonic information for current timing position.
   * Determines chord based on whether we're in fill portion of phrase.
   */
  getKeyInfo(timingInfo: TimingInfo): KeyInfo {
    const currentChord = this.determineCurrentChord(timingInfo);
    const scale = this.getScaleForChord(currentChord);

    return {
      rootNote: this.getCurrentRoot(currentChord),
      scale,
      currentChord,
    };
  }

  /**
   * Set the root note for the key center.
   * All harmony will be relative to this note.
   */
  setRootNote(noteNumber: number): void {
    this.rootNote = noteNumber;
  }

  /**
   * Get the current root note.
   */
  getRootNote(): number {
    return this.rootNote;
  }

  /**
   * Determine current chord based on phrase position.
   *
   * Rule: Use V chord during fill portion of phrase for harmonic excitement.
   * This creates tension that resolves when next phrase begins.
   *
   * Examples:
   * - Groove portion: I chord (stable foundation)
   * - Fill portion: V chord (adds tension and excitement)
   */
  private determineCurrentChord(timingInfo: TimingInfo): "I" | "V" {
    // V chord during fills for harmonic movement and excitement
    return PhraseManager.isInFill(timingInfo) ? "V" : "I";
  }

  /**
   * Get the appropriate scale for the current chord.
   *
   * - I chord: Major scale (bright, stable)
   * - V chord: Mixolydian scale (has b7 for dominant flavor)
   */
  private getScaleForChord(chord: "I" | "V"): number[] {
    return chord === "I" ? this.majorScale : this.mixolydianScale;
  }

  /**
   * Get the root note for the current chord.
   *
   * - I chord: Use the key's root note
   * - V chord: Use the fifth degree (7 semitones up)
   */
  private getCurrentRoot(chord: "I" | "V"): number {
    return chord === "I" ? this.rootNote : this.rootNote + 7;
  }

  /**
   * Get chord tones for the current harmony.
   * Useful for composers that want to emphasize harmonic notes.
   */
  getChordTones(keyInfo: KeyInfo): number[] {
    const root = keyInfo.rootNote;

    if (keyInfo.currentChord === "I") {
      // I chord: 1-3-5 of major scale
      return [root, root + 4, root + 7];
    } else {
      // V chord: 1-3-5-b7 of mixolydian (dominant 7th)
      return [root, root + 4, root + 7, root + 10];
    }
  }

  /**
   * Check if a note is a chord tone in the current harmony.
   * Useful for composers to emphasize consonant notes.
   */
  isChordTone(noteNumber: number, keyInfo: KeyInfo): boolean {
    const chordTones = this.getChordTones(keyInfo);
    const noteInOctave = noteNumber % 12;

    return chordTones.some((chordTone) => chordTone % 12 === noteInOctave);
  }

  /**
   * Get the scale degree (1-7) of a note in the current key.
   * Returns 0 if the note is not in the scale.
   */
  getScaleDegree(noteNumber: number, keyInfo: KeyInfo): number {
    const noteInOctave = noteNumber % 12;
    const rootInOctave = keyInfo.rootNote % 12;
    const intervalFromRoot = (noteInOctave - rootInOctave + 12) % 12;

    const scaleIndex = keyInfo.scale.indexOf(intervalFromRoot);
    return scaleIndex >= 0 ? scaleIndex + 1 : 0;
  }

  /**
   * Get a note at a specific scale degree in the current key.
   * Useful for composers to build melodies within the key.
   */
  getNoteAtScaleDegree(
    degree: number,
    octave: number,
    keyInfo: KeyInfo,
  ): number {
    if (degree < 1 || degree > keyInfo.scale.length) {
      throw new Error(`Scale degree ${degree} out of range for current scale`);
    }

    const scaleInterval = keyInfo.scale[degree - 1];
    return keyInfo.rootNote + (octave - 4) * 12 + scaleInterval;
  }
}
